import styled, { css } from 'styled-components';
import Typography from '@/components/core/Typography/Typography';
import { Flex, FlexCol3XS } from '@/components/Flex';
import {
    ChangeEventHandler,
    FocusEvent,
    ForwardedRef,
    forwardRef,
    KeyboardEventHandler,
    MouseEventHandler,
    useRef,
    useState
} from 'react';

export const InputBox = styled.div<{
    $focused?: boolean;
    $disabled?: boolean;
    $hasValue?: boolean;
    $hasError?: boolean;
    $size?: 'md' | 'sm';
    $withIncreasedHorizontalPadding?: boolean;
}>`
    height: ${({ $size, $withIncreasedHorizontalPadding }) => $withIncreasedHorizontalPadding ? '48px' : ($size === 'sm' ? '28px' : '36px')};
    background-color: ${({ theme }) => theme.colors.palette.white};
    border-radius: ${({ theme }) => theme.extras.radius.sm};
    padding: ${({ theme, $size, $withIncreasedHorizontalPadding }) => $withIncreasedHorizontalPadding ? theme.spacing['2xs'] : ($size === 'sm' ? '6px' : '6px 12px')};
    transition: box-shadow 0.2s ease-in-out;
    box-sizing: border-box;
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing['4xs']};
    
    font-size: ${({ theme, $size }) => theme.typography.uiType[$size === 'sm' ? 'xs' : 'sm'].R.size};
    line-height: ${({ theme, $size }) => theme.typography.uiType[$size === 'sm' ? 'xs' : 'sm'].R.lineHeight};
    letter-spacing: ${({ theme, $size }) => theme.typography.uiType[$size === 'sm' ? 'xs' : 'sm'].R.letterSpacing ?? 'normal'};
    font-weight: ${({ theme }) => theme.typography.weight.regular};

    box-shadow: inset 0 0 0 1px ${({
        theme,
        $hasValue
    }) => theme.colors.palette[`grey_${$hasValue ? 500 : 400}`]};

    ${({ $disabled }) => $disabled && css`
        opacity: ${({ theme }) => theme.extras.opacity.disabled};
        cursor: default;

        input {
            cursor: default;
        }
    `};

    ${({
        $disabled,
        $hasError
    }) => !$disabled && $hasError && css`
        box-shadow: inset 0 0 0 2px ${({ theme }) => theme.colors.palette.red_100};
        border-color: transparent;
    `};

    ${({
        $disabled,
        $hasError,
        $focused
    }) => !$disabled && !$hasError && (
        $focused ? css`
                box-shadow: inset 0 0 0 2px ${({ theme }) => theme.colors.primary.focused} !important;
            ` : css`
                &:hover {
                    box-shadow: inset 0 0 0 2px ${({ theme }) => theme.colors.palette.grey_500};
                    border-color: transparent;
                }
            `
    )};

    ::placeholder {
        transition: color 0.2s ease-in-out;
        color: ${({ theme }) => theme.colors.palette.grey_600};

        // override firefox default style
        opacity: 1;
    }

    input {
        width: 100%;
        border: none;
        color: ${({ theme }) => theme.colors.palette.grey_700};
        background-color: transparent;

        &:focus-visible {
            outline: none;
        }

        &::placeholder {
            color: ${({ theme }) => theme.colors.palette.grey_600};
        }

        color: ${({ theme, $hasError }) => theme.colors.palette[$hasError ? 'red_300' : 'grey_700']}};
    }

    input:-webkit-autofill {
        //hide autofiled background
        box-shadow: 0 0 0 30px ${({ theme }) => theme.colors.palette.white} inset;
        //auto-filled text color
        -webkit-text-fill-color: ${({ theme, $hasError }) => theme.colors.palette[$hasError ? 'red_300' : 'grey_700']};
    }

    /* Chrome, Safari, Edge, Opera */

    input::-webkit-outer-spin-button,
    input::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }

    /* Firefox */

    input[type=number] {
        -moz-appearance: textfield;
    }
`;

const LabelTypography = styled(Typography)`
    height: 16px;
`;

const LabelAsterisk = styled(Typography)`
    color: ${({ theme }) => theme.colors.palette.grey_600};
`;

const RootLabel = styled.label`
    width: 100%;
`;

export const Hint = styled(Typography)<{ $hasError?: boolean }>`
    padding: 0 ${({ theme }) => theme.spacing['2xs']};
    transition: color 0.2s ease-in-out;

    ${({ $hasError }) => $hasError ? css`
        color: ${({ theme }) => theme.colors.palette.red_300};
    ` : css`
        color: ${({ theme }) => theme.colors.palette.grey_600};
    `}
`;

export type TextInputProps = {
    variant?: 'default' | 'underline';
    label?: string;
    hint?: string;
    isRequired?: boolean;
    error?: boolean;
    placeholder?: string;
    type?: 'text' | 'password' | 'email' | 'number' | 'tel' | 'hidden';
    startAdornment?: React.ReactNode;
    endAdornment?: React.ReactNode;
    form?: string;
    rootClassName?: string;
    inputBoxClassName?: string;
    visuallyFocused?: boolean;
    size?: 'sm' | 'md';
    hideEndAdornmentWhenNoValue?: boolean;
    withIncreasedHorizontalPadding?: boolean;
    disabled: boolean;
    pattern: RegExp;
    onFocus: Function;
    onBlur: Function;
    onClick?: MouseEventHandler;
    onChange?: ChangeEventHandler<HTMLInputElement>;
    value: any;
    onKeyDown?: KeyboardEventHandler<HTMLInputElement>;
    onKeyPress?: KeyboardEventHandler<HTMLInputElement>;
    step?: string;
    autocomplete?: string;
}

const TextInput = forwardRef<HTMLInputElement, Partial<TextInputProps>>((props: Partial<TextInputProps>, forwardedRef: ForwardedRef<HTMLInputElement>) => {
    const {
        label,
        placeholder,
        isRequired = false,
        error = false,
        type = 'text',
        endAdornment,
        startAdornment,
        rootClassName = '',
        inputBoxClassName = '',
        hint,
        disabled,
        pattern,
        visuallyFocused = false,
        size = 'md',
        withIncreasedHorizontalPadding = false,
        hideEndAdornmentWhenNoValue = false,

        onFocus = () => { },
        onBlur = () => { },

        ...rest
    } = props;

    const [focused, setFocused] = useState(false);

    const handleFocus = (e: FocusEvent<HTMLInputElement>) => {
        setFocused(true);
        onFocus(e);
    };

    const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
        setFocused(false);
        onBlur(e);
    };

    const handleInput = (e) => {
        if (pattern && e.data && !pattern.test(e.data)) {
            e.preventDefault();
        }
    };

    const fallbackRef = useRef(null);
    const ref = forwardedRef || fallbackRef;

    const renderEndAdornment = () => {
        if (hideEndAdornmentWhenNoValue) {
            return (Boolean(rest.value?.length) && endAdornment);
        } else {
            return endAdornment;
        }
    };

    return (
        <RootLabel className={rootClassName}>
            <FlexCol3XS>
                {label &&
                    <Flex>
                        {label && <LabelTypography variant='uiType' size='xs' modifier='R'>{label}</LabelTypography>}
                        {isRequired && <LabelAsterisk variant='uiType' size='xs' modifier='R'>*</LabelAsterisk>}
                    </Flex>
                }
                <InputBox
                  $disabled={disabled}
                  $focused={focused || visuallyFocused}
                  $hasValue={!!rest.value}
                  $hasError={error}
                  $size={size}
                  $withIncreasedHorizontalPadding={withIncreasedHorizontalPadding}
                  className={inputBoxClassName}
                >
                    {startAdornment}

                    <input
                      ref={ref}
                      type={type}
                      placeholder={placeholder}
                      disabled={disabled}
                      onFocus={handleFocus}
                      onBlur={handleBlur}
                      onBeforeInput={handleInput}
                      data-error={error || undefined}
                      {...rest}
                    />

                    {renderEndAdornment()}
                </InputBox>
                {hint && (
                    <Hint
                      variant='uiType'
                      size='xs'
                      modifier='R'
                      $hasError={error}
                      role={error ? 'alert' : undefined}
                      data-testid={error ? 'validation-error' : undefined}
                    >
                        {hint}
                    </Hint>
                )}
            </FlexCol3XS>
        </RootLabel>
    );
});

TextInput.displayName = 'TextInput';

export default TextInput;
